import type { PhaseDto } from '@/api/generated';
import { usePhases } from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { useTweaks } from '@/lib/tweaks';
import styles from './TimelineStrip.module.css';

interface TimelineStripProps {
  /** Phase id (lowercase) to highlight as "current". When omitted, the
   *  strip auto-detects the Doing phase, falling back to the first Idle. */
  current?: string;
  /** Overrides the tweaks store. When omitted, follows the user's tweak. */
  variant?: 'milestones' | 'gantt';
}

interface StripPhase {
  id: string;
  label: string;
  status: 'done' | 'doing' | 'late' | 'idle';
  progress: number;
  startMs: number;
  endMs: number;
  /** Short "mm" month string for the milestones-view label. */
  monthLabel: string;
}

function toNum(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toMs(d: string | undefined): number {
  if (!d) return 0;
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : 0;
}

function shapePhases(phases: PhaseDto[]): StripPhase[] {
  return phases.map((p) => {
    const startMs = toMs(p.start);
    return {
      id: String(p.id).toLowerCase(),
      label: p.label,
      status: (String(p.status).toLowerCase() as StripPhase['status']) ?? 'idle',
      progress: toNum(p.progress),
      startMs,
      endMs: toMs(p.end),
      monthLabel: startMs > 0 ? String(new Date(startMs).getMonth() + 1).padStart(2, '0') : '—',
    };
  });
}

/**
 * 2026-05-17 — TimelineStrip used to render from a static PHASES constant
 * with hardcoded status / progress / month positions. It now reads
 * `usePhases()` so the strip on the dashboard + the gantt on /tijdlijn/gantt
 * reflect real DB state. The gantt bars are positioned proportionally to
 * the timeline's own min/max date window so any project — long or short —
 * fits the strip without depending on the original 35-month design assumption.
 */
export function TimelineStrip({ current, variant }: TimelineStripProps) {
  const tweakStyle = useTweaks((s) => s.timelineStyle);
  const mode = variant ?? tweakStyle;
  const phasesQuery = usePhases();

  const raw = phasesQuery.data ?? [];
  if (raw.length === 0) {
    return (
      <div className="mono" style={{ padding: 12, color: 'var(--ink-3)' }}>
        {phasesQuery.isPending ? 'Tijdlijn laden…' : 'Geen fases geconfigureerd.'}
      </div>
    );
  }

  const shaped = shapePhases(raw);

  // Pick the "current" highlight: explicit prop wins; otherwise the first
  // Doing phase; otherwise the first Idle phase; otherwise the first row.
  const auto =
    shaped.find((p) => p.status === 'doing') ??
    shaped.find((p) => p.status === 'idle') ??
    shaped[0];
  const currentId = (current ?? auto?.id ?? '').toLowerCase();

  if (mode === 'gantt') {
    // Project window = min start → max end across the phase set. Bars are
    // positioned proportionally so a 6-month renovation and a 4-year build
    // both fill the strip cleanly.
    const startsWithDates = shaped.map((p) => p.startMs).filter((m) => m > 0);
    const endsWithDates = shaped.map((p) => p.endMs).filter((m) => m > 0);
    const minStart = startsWithDates.length > 0 ? Math.min(...startsWithDates) : 0;
    const maxEnd = endsWithDates.length > 0 ? Math.max(...endsWithDates) : 1;
    const span = Math.max(1, maxEnd - minStart);

    return (
      <div className={`col gap-4 ${styles.gantt}`}>
        {shaped.map((p) => {
          const isCurrent = p.id === currentId;
          const left = p.startMs > 0 ? ((p.startMs - minStart) / span) * 100 : 0;
          const width =
            p.endMs > 0 && p.startMs > 0
              ? Math.max(2, ((p.endMs - p.startMs) / span) * 100)
              : 4;
          const fill =
            p.status === 'done'
              ? 'var(--ok)'
              : p.status === 'doing'
                ? 'var(--warn)'
                : 'var(--idle)';
          return (
            <div key={p.id} className="row gap-8">
              <div className={`${styles.ganttLabel} ${isCurrent ? styles.ganttLabelActive : ''}`}>
                {p.label}
              </div>
              <div className={styles.ganttTrack}>
                <div
                  className={styles.ganttBar}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    background: fill,
                    opacity: p.status === 'idle' ? 0.4 : 1,
                  }}
                />
                {p.status === 'doing' && p.progress > 0 && (
                  <div
                    className={styles.ganttProgress}
                    style={{
                      left: `${left}%`,
                      width: `${width * p.progress}%`,
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.milestonesWrap}>
      <div className={styles.milestonesTrack} />
      <div className={styles.milestonesTrackFilled} />
      <div className={styles.milestonesRow}>
        {shaped.map((p) => {
          const isCurrent = p.id === currentId;
          const done = p.status === 'done';
          const doing = p.status === 'doing';
          const cls = [
            styles.milestone,
            done ? styles.milestoneDone : '',
            doing ? styles.milestoneDoing : '',
            isCurrent ? styles.milestoneCurrent : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div key={p.id} className={styles.milestoneCell}>
              <div className={cls}>
                {done && <Icon name="check" size={9} color="var(--paper)" strokeWidth={2.5} />}
              </div>
              <div className={styles.milestoneLabel}>{p.label}</div>
              <div className={`mono ${styles.milestoneDate}`}>{p.monthLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
